'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { toast } from '@/components/ui/toast'
import { canAccess } from '@/lib/roles'
import { useTranslations } from 'use-intl'

/**
 * Client-side route guard (Rule 12 §3). When the signed-in roles may not open
 * the current route (e.g. an AP-Operator typing /ai/policies), redirect to the
 * dashboard with a toast. The API is enforced independently by the backend
 * authz map; this only keeps the UI from rendering dead pages.
 */
export function RoleGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const t = useTranslations('shell.roleGuard')
  const { data: session, status } = useSession()
  const roles = session?.roles
  const lastBlocked = useRef<string | null>(null)

  const allowed = status !== 'authenticated' || canAccess(pathname ?? '/', roles)

  useEffect(() => {
    if (allowed) {
      lastBlocked.current = null
      return
    }
    if (lastBlocked.current === pathname) return
    lastBlocked.current = pathname
    toast.warning(t('unavailableTitle'), {
      description: t('unavailableDescription'),
    })
    router.replace('/')
  }, [allowed, pathname, router, t])

  if (!allowed) return null
  return <>{children}</>
}
